import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { CardImage } from '@/components/CardImage';
import { RarityBadge } from '@/components/RarityBadge';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { cardPath } from '@/lib/cardSlug';
import { setCodeFromSlug, setPath, slugify } from '@/lib/setSlug';

const SITE = 'https://lorescout.com';

interface SetRow {
  id: string;
  name: string;
  code: string;
  released_at: string | null;
  total_cards: number | null;
  treatments_available: string[] | null;
}

function formatDate(iso: string | null): string {
  if (!iso) return 'TBA';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function SetPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const code = setCodeFromSlug(slug);

  const { data, isLoading } = useQuery({
    queryKey: ['set-page', code],
    staleTime: 10 * 60 * 1000,
    enabled: !!code,
    queryFn: async () => {
      const { data: sets } = await supabase
        .from('sets')
        .select('*')
        .ilike('code', code)
        .limit(1);
      const set = (sets || [])[0] as SetRow | undefined;
      if (!set) return null;

      // Paginated card fetch by set_name
      const PAGE = 1000;
      let from = 0;
      const all: any[] = [];
      while (true) {
        const { data: page } = await supabase
          .from('cards')
          .select('id,name,subtitle,rarity,ink_color,cost,image_url,enchanted')
          .eq('set_name', set.name)
          .order('name')
          .range(from, from + PAGE - 1);
        if (!page || !page.length) break;
        all.push(...page);
        if (page.length < PAGE) break;
        from += PAGE;
      }
      return { set, cards: all };
    },
  });

  if (isLoading) {
    return <div className="app-container min-h-screen flex items-center justify-center"><LoadingSpinner /></div>;
  }

  if (!data?.set) {
    return (
      <div className="app-container min-h-screen p-6 text-center">
        <Helmet><title>Set not found · LoreScout</title><meta name="robots" content="noindex" /></Helmet>
        <h1 className="text-2xl font-serif mt-12">Set not found</h1>
        <Link to="/sets" className="underline">Browse all sets</Link>
      </div>
    );
  }

  const { set, cards } = data;
  const canonical = `${SITE}${setPath(set)}`;
  const description = `${set.name} (Set ${set.code}) — full Disney Lorcana card list with live prices. ${cards.length} cards. Released ${formatDate(set.released_at)}.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${set.name} — Disney Lorcana`,
    description,
    url: canonical,
    isPartOf: { '@type': 'WebSite', name: 'LoreScout', url: SITE },
    hasPart: cards.slice(0, 50).map(c => ({
      '@type': 'Product',
      name: c.subtitle ? `${c.name} — ${c.subtitle}` : c.name,
      url: `${SITE}${cardPath(c)}`,
      image: c.image_url || undefined,
    })),
  };

  return (
    <div className="app-container min-h-screen pb-12">
      <Helmet>
        <title>{`${set.name} (${set.code}) · Lorcana · LoreScout`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${set.name} — Disney Lorcana Set ${set.code}`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <header className="flex items-center gap-3 p-4 border-b border-border">
        <Link to="/" className="text-sm underline">← LoreScout</Link>
        <span className="text-xs text-muted-foreground ml-auto">Set {set.code}</span>
      </header>

      <article className="px-4 pt-4">
        <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link>
          {' / '}
          <Link to="/sets" className="hover:underline">Sets</Link>
          {' / '}
          <span className="text-foreground">{set.name}</span>
        </nav>

        <h1 className="font-serif text-3xl leading-tight">{set.name}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Disney Lorcana Set {set.code} · Released {formatDate(set.released_at)} · {cards.length} cards
        </p>

        {set.treatments_available?.length ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Treatments: {set.treatments_available.join(', ').replace(/_/g, ' ')}
          </p>
        ) : null}

        <section className="mt-6">
          <h2 className="font-serif text-xl mb-3">All cards in {set.name}</h2>
          {cards.length === 0 ? (
            <p className="text-sm text-muted-foreground">No cards published for this set yet.</p>
          ) : (
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {cards.map(c => (
                <li key={c.id}>
                  <Link to={cardPath(c)} className="block group">
                    <CardImage
                      src={c.image_url}
                      alt={`${c.name}${c.subtitle ? ` — ${c.subtitle}` : ''} (${set.name})`}
                      inkColor={c.ink_color}
                      enchanted={!!c.enchanted}
                    />
                    <div className="mt-1 text-xs leading-tight">
                      <div className="truncate font-medium group-hover:underline">{c.name}</div>
                      {c.subtitle && <div className="truncate text-muted-foreground italic">{c.subtitle}</div>}
                      <div className="mt-1 flex items-center gap-1.5">
                        <RarityBadge rarity={c.rarity} />
                        {c.cost != null && <span className="text-[10px] text-muted-foreground">Cost {c.cost}</span>}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="mt-10 rounded-lg border border-border p-4 text-sm">
          <h2 className="font-serif text-lg mb-1">Track {set.name} in your collection</h2>
          <p className="text-muted-foreground">
            LoreScout tracks live market prices and history for every rarity and treatment in {set.name} — Cold Foil,
            Enchanted, Epic, Iconic, Rainbow Foil and more.
          </p>
          <Link to="/" className="inline-block mt-3 underline">Open LoreScout →</Link>
        </section>
      </article>
    </div>
  );
}

// Re-export slugify so the index doesn't need to import from two files.
export { slugify };
